import { GoogleGenerativeAI } from "@google/generative-ai";

// Initialize the Gemini AI
const genAI = new GoogleGenerativeAI(
  process.env.REACT_APP_VISURE_DETAILS_GEMINI_API_KEY,
);

/**
 * Converts a file to base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 encoded string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Analyzes an uploaded bill image using Gemini Vision API
 * Extracts key information for energy/gas bills
 * @param {File} file - The uploaded bill image file
 * @returns {Promise<Object>} - Extracted bill data
 */
export const analyzeBill = async (file) => {
  try {
    // Convert file to base64
    const base64Data = await fileToBase64(file);

    // Get the file type
    const mimeType = file.type || "image/jpeg";

    // Use Gemini model with vision capabilities
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

    const prompt = `Analizza questa bolletta di energia elettrica o gas e estrai le seguenti informazioni in formato JSON.

SCHEMA JSON COMPLETO DA RESTITUIRE:
{
  "tipoServizio": "luce" | "gas" | "luce-gas",
  "fornitore": "nome esatto del fornitore energetico",
  "nomeIntestatario": "nome dell'intestatario",
  "cognomeIntestatario": "cognome dell'intestatario",
  "ragioneSociale": "ragione sociale se cliente business",
  "codiceFiscale": "codice fiscale completo",
  "indirizzo": "indirizzo completo di fornitura (via, numero civico)",
  "cap": "codice postale",
  "comune": "città/comune di fornitura",
  "cittaFornitura": "città della fornitura (stesso valore di comune)",
  "telefono": "numero di telefono",
  "email": "indirizzo email",
  
  "pod": "codice POD per elettricità (formato IT001E...)",
  "pdr": "codice PDR per gas",
  "tipoMercato": "tutelato" | "libero",
  "tipologia": "privato" | "business",
  
  "numeroContratto": "numero identificativo del contratto",
  "dataAttivazione": "data attivazione contratto YYYY-MM-DD",
  "tipoPrezzo": "fisso" | "variabile",
  "scadenzaOfferta": "data scadenza offerta YYYY-MM-DD",
  
  "dataFattura": "data emissione fattura YYYY-MM-DD",
  "dataInizio": "data inizio periodo fatturato YYYY-MM-DD",
  "dataFine": "data fine periodo fatturato YYYY-MM-DD",
  "giorni": "numero esatto di giorni del periodo fatturato",
  "totaleBolletta": "importo totale da pagare in euro (numero decimale)",
  
  "totaleKwh": "totale kWh consumati nel periodo (solo luce, numero decimale)",
  "f1": "consumo fascia F1 in kWh (numero decimale)",
  "f2": "consumo fascia F2 in kWh (numero decimale)",
  "f3": "consumo fascia F3 in kWh (numero decimale)",
  "potenzaImpegnata": "potenza impegnata contrattuale in kW (numero decimale)",
  "potenzaDisponibile": "potenza disponibile in kW (numero decimale)",
  
  "costoEnergia": "costo unitario energia elettrica €/kWh (numero decimale con 6 decimali se possibile)",
  "costoGas": "costo unitario gas €/Smc (numero decimale)",
  "totaleMateriaPrima": "totale spesa per materia prima/energia in euro (numero decimale)",
  "pcvMensile": "Prezzo Commercializzazione Vendita mensile in euro (numero decimale)",
  "totaleSpesaFissa": "totale spese fisse (trasporto, oneri, imposte) in euro (numero decimale)",
  
  "consumoAnnuale": "stima consumo annuale in kWh o Smc",
  "stimaAnnua": "stima spesa annua calcolata: (totaleBolletta / giorni) * 365"
}

ISTRUZIONI CRITICHE:
1. Restituisci SOLO il JSON, nessun altro testo prima o dopo
2. Se un campo non è presente o illeggibile, usa null (non stringhe vuote)
3. Per i numeri usa valori numerici, non stringhe (es: 38.85 non "38.85")
4. Per tipoServizio: identifica se è bolletta luce, gas o entrambi
5. Per fornitore: riconosci tra Enel, Plenitude, Iren, Edison, Acea, Sorgenia, A2A, Rubino Energas, Wekiwi, Illumia, etc.
6. Per tipoMercato: "libero" se mercato libero, "tutelato" se maggior tutela
7. Per tipologia: "privato" per uso domestico, "business" per aziende/partite IVA
8. Per i consumi elettrici: cerca le fasce orarie F1, F2, F3 (potrebbero essere chiamate Peak, Mid-Peak, Off-Peak)
9. Per costoEnergia: cerca il costo unitario dell'energia (PED, Prezzo Energia Domestica) espresso in €/kWh
10. Per totaleMateriaPrima: somma tutti i costi relativi alla vendita energia/gas
11. Per pcvMensile: cerca PCV o "Commercializzazione Vendita"
12. Per totaleSpesaFissa: somma trasporto, distribuzione, oneri di sistema, accise, IVA
13. Calcola stimaAnnua con la formula: (totaleBolletta / giorni) * 365
14. Assicurati che il JSON sia valido e parsabile
15. Non inventare dati: se manca, metti null`;

    const imagePart = {
      inlineData: {
        data: base64Data,
        mimeType: mimeType,
      },
    };

    const result = await model.generateContent([prompt, imagePart]);
    const response = await result.response;
    const text = response.text();

    // Clean the response to extract JSON
    let jsonText = text.trim();

    // Remove markdown code blocks if present
    if (jsonText.startsWith("```json")) {
      jsonText = jsonText.replace(/```json\n?/g, "").replace(/```\n?/g, "");
    } else if (jsonText.startsWith("```")) {
      jsonText = jsonText.replace(/```\n?/g, "");
    }

    // Parse the JSON response
    const extractedData = JSON.parse(jsonText);

    return {
      success: true,
      data: extractedData,
    };
  } catch (error) {
    console.error("Error analyzing bill:", error);
    return {
      success: false,
      error: error.message || "Errore durante l'analisi della bolletta",
    };
  }
};

/**
 * Maps extracted bill data to form data structure
 * @param {Object} billData - Data extracted from bill
 * @returns {Object} - Form data object
 */
export const mapBillDataToForm = (billData) => {
  if (!billData) return {};

  return {
    servizio: billData.tipoServizio || "luce",
    fornitore: mapFornitore(billData.fornitore),
    nome: billData.nomeIntestatario || "",
    cognome: billData.cognomeIntestatario || "",
    codiceFiscale: billData.codiceFiscale || "",
    indirizzo: billData.indirizzo || "",
    cap: billData.cap || "",
    comune: billData.comune || "",
    telefono: billData.telefono || "",
    email: billData.email || "",
    mercato: billData.tipoMercato || "libero",
    tipologia: billData.tipologia || "privato",
    // Store all extracted data for use in details dialog
    _billMetadata: {
      // Intestatario
      ragioneSociale: billData.ragioneSociale,
      cittaFornitura: billData.cittaFornitura || billData.comune,
      cf: billData.codiceFiscale,
      
      // Fornitura
      pod: billData.pod,
      pdr: billData.pdr,
      
      // Contratto
      numeroContratto: billData.numeroContratto,
      dataAttivazione: billData.dataAttivazione,
      tipoPrezzo: billData.tipoPrezzo,
      scadenzaOfferta: billData.scadenzaOfferta,
      
      // Periodo e date
      dataFattura: billData.dataFattura,
      dataInizio: billData.dataInizio,
      dataFine: billData.dataFine,
      giorni: billData.giorni,
      
      // Consumi elettrici
      totaleKwh: billData.totaleKwh,
      f1: billData.f1,
      f2: billData.f2,
      f3: billData.f3,
      potenzaImpegnata: billData.potenzaImpegnata,
      potenzaDisponibile: billData.potenzaDisponibile,
      
      // Costi
      totaleBolletta: billData.totaleBolletta,
      costoEnergia: billData.costoEnergia,
      costoGas: billData.costoGas,
      totaleMateriaPrima: billData.totaleMateriaPrima,
      pcvMensile: billData.pcvMensile,
      totaleSpesaFissa: billData.totaleSpesaFissa,
      consumoAnnuale: billData.consumoAnnuale,
      stimaAnnua: billData.stimaAnnua,
      
      // Metadati (generati dal sistema)
      dataCaricamento: new Date().toLocaleString("it-IT"),
      versione: 1,
      idUtente: "admin_user_01", // TODO: Get from auth context
      qualitaRiconoscimento: determineQuality(billData),
    },
  };
};

/**
 * Determines the quality of recognition based on how many fields were extracted
 * @param {Object} billData - Extracted bill data
 * @returns {string} - Quality level: "alta", "media", "bassa"
 */
const determineQuality = (billData) => {
  if (!billData) return "bassa";
  
  const criticalFields = [
    "fornitore", "nomeIntestatario", "cognomeIntestatario", 
    "totaleBolletta", "dataInizio", "dataFine"
  ];
  
  const filledCriticalFields = criticalFields.filter(field => 
    billData[field] !== null && billData[field] !== undefined && billData[field] !== ""
  ).length;
  
  const percentage = (filledCriticalFields / criticalFields.length) * 100;
  
  if (percentage >= 75) return "alta";
  if (percentage >= 50) return "media";
  return "bassa";
};

/**
 * Maps supplier name from bill to form value
 * @param {string} supplierName - Supplier name from bill
 * @returns {string} - Mapped supplier ID
 */
const mapFornitore = (supplierName) => {
  if (!supplierName) return "";

  const normalized = supplierName.toLowerCase().trim();

  if (normalized.includes("enel")) return "enel";
  if (normalized.includes("plenitude") || normalized.includes("eni"))
    return "plenitude";
  if (normalized.includes("iren")) return "iren";
  if (normalized.includes("edison")) return "edison";
  if (normalized.includes("acea")) return "acea";
  if (normalized.includes("sorgenia")) return "sorgenia";
  if (normalized.includes("a2a")) return "a2a";

  // If not found, return "altro"
  return "altro";
};
